import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import api from "@/lib/api";

const KEY = "dp_popup_seen";

/**
 * Site-wide popup ad. Shows once per session, only after the user has
 * scrolled past N% of the page (default 50%). Fully admin-controlled:
 * enabled flag + image/link OR raw HTML slot for an AdSense unit.
 */
export default function PopupAd() {
  const [cfg, setCfg] = useState(null);
  const [open, setOpen] = useState(false);
  const shown = useRef(false);

  // Load config
  useEffect(() => {
    api.get("/public/settings")
      .then((r) => setCfg(r.data?.popup_ad || null))
      .catch(() => setCfg(null));
  }, []);

  // Scroll trigger
  useEffect(() => {
    if (!cfg || !cfg.enabled) return;
    try {
      if (sessionStorage.getItem(KEY) === "1") return;
    } catch (_) {}

    const pct = Math.max(10, Math.min(95, cfg.scroll_percent || 50));
    let userScrolled = false;

    const onScroll = () => {
      if (shown.current) return;
      userScrolled = true;
      const doc = document.documentElement;
      const scrolled = window.scrollY + window.innerHeight;
      const total = doc.scrollHeight;
      if (total <= 0) return;
      const ratio = (scrolled / total) * 100;
      if (ratio >= pct) {
        shown.current = true;
        setOpen(true);
        try { sessionStorage.setItem(KEY, "1"); } catch (_) {}
        window.removeEventListener("scroll", onScroll);
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [cfg]);

  if (!cfg || !cfg.enabled) return null;

  const hasImage = !!cfg.image_url;
  const hasHtml = !!cfg.html;
  if (!hasImage && !hasHtml) return null;

  const close = () => setOpen(false);

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            className="fixed inset-0 z-[120] bg-black/60"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={close}
            data-testid="popup-ad-backdrop"
          />
          <motion.div
            data-testid="popup-ad"
            initial={{ opacity: 0, scale: 0.94, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.94, y: 20 }}
            transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
            className="fixed z-[121] inset-0 flex items-center justify-center px-4 pointer-events-none"
          >
            <div className="relative w-full max-w-[520px] bg-white border border-[#0F0F0F] shadow-2xl pointer-events-auto">
              <button
                onClick={close}
                data-testid="popup-ad-close"
                aria-label="विज्ञापन बंद करें"
                className="absolute -top-3 -right-3 z-10 w-9 h-9 rounded-full bg-[#0F0F0F] text-white flex items-center justify-center hover:bg-[#D92525] transition-colors shadow-lg"
              >
                <X size={16} />
              </button>
              <div className="px-4 pt-3 pb-2 text-[10px] uppercase tracking-[0.24em] text-[#4A4A4A] flex items-center justify-between">
                <span>{cfg.title || "विज्ञापन · Advertisement"}</span>
              </div>
              {hasImage ? (
                <a
                  href={cfg.link_url || "#"}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="popup-ad-link"
                  className="block"
                >
                  <img
                    src={cfg.image_url}
                    alt={cfg.title || "Advertisement"}
                    className="w-full h-auto max-h-[70vh] object-contain bg-[#F4F4F0]"
                  />
                </a>
              ) : (
                <div
                  className="px-4 pb-4"
                  data-testid="popup-ad-html"
                  // Admin-supplied AdSense/HTML slot
                  dangerouslySetInnerHTML={{ __html: cfg.html }}
                />
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
